/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
  Divider,
  Tag,
  Space,
  Typography,
} from 'antd';
import {
  deleteTiposDeRecargosOmitidos,
  getTiposDeRecargosOmitidos,
  patchTiposDeRecargosOmitidos,
  postTiposDeRecargosOmitidos,
} from 'api/configuracion/tiposDeRecargosOmitidos';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { getTiposDeRecargos } from 'api/recaudacion/tiposDeRecargo';
import Select from 'components/Select';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import NumericInput from 'components/NumericInput';
import { getTiposDeActualizaciones } from 'api/recaudacion/tiposDeActualizacion';

export const permissionConfig = {
  permissionModel: 'tipoderecargoomitido',
  catalogsModels: ['tipoderecargo', 'periodofiscal'],
};

// ─── Hook ────────────────────────────────────────────────────────────────────

const useTiposDeRecargosOmitidos = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleModalDelete, setVisibleModalDelete] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tiposDeRecargos, setTiposDeRecargos] = useState([]);
  const [tiposDeActualizaciones, setTiposDeActualizaciones] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const _data = await getTiposDeRecargosOmitidos();
    setData(_data);
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      const [
        _tiposDeRecargos,
        _tiposDeActualizaciones,
        _data,
      ] = await Promise.all([
        getTiposDeRecargos(),
        getTiposDeActualizaciones(),
        getTiposDeRecargosOmitidos(),
      ]);
      if (mounted) {
        setTiposDeRecargos(_tiposDeRecargos);
        setTiposDeActualizaciones(_tiposDeActualizaciones);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const reset = useCallback(() => {
    setVisible(false);
    setVisibleModalDelete(false);
    setSelectedRowKeys([]);
    form.resetFields();
  }, [form]);

  const onFinish = useCallback(async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      const { fechas, ...rest } = values;
      const [fechaInicial, fechaFinal] = fechas;

      const formattedValues = {
        ...rest,
        fecha_inicial: fechaInicial,
        fecha_final: fechaFinal,
      };

      const saved = await (key
        ? patchTiposDeRecargosOmitidos(key, formattedValues)
        : postTiposDeRecargosOmitidos(formattedValues));
      if (saved) {
        reset();
        await fetchData();
        return;
      }
    } catch (err) {
      onError(err, setLoading);
    }
    setLoading(false);
  }, [form, selectedRowKeys, reset, fetchData]);

  const deleteItem = useCallback(async () => {
    const [key] = selectedRowKeys;
    if (!key) return;
    setLoading(true);
    const deleted = await deleteTiposDeRecargosOmitidos(key);
    if (deleted) {
      reset();
      await fetchData();
    }
  }, [selectedRowKeys, reset, fetchData]);

  const handleOnRowClick = useCallback((record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue({
      ...record,
      fechas: [record.fecha_inicial, record.fecha_final],
      tipos_de_recargo: record.tipos_de_recargo.map((item) => item.id),
      tipos_de_actualizaciones: record?.tipos_de_actualizaciones?.map((item) => item.id),
    });
  }, [form]);

  const validatePeriodoUnico = useCallback((_, value) => {
    if (!value) return Promise.resolve();
    const [currentKey] = selectedRowKeys;
    const duplicate = data.some(
      (item) => String(item.periodo) === String(value) && item.id !== currentKey,
    );
    return duplicate
      ? Promise.reject(new Error('Ya existe una configuración para este periodo.'))
      : Promise.resolve();
  }, [data, selectedRowKeys]);

  return {
    form,
    loading,
    data,
    visible,
    visibleModalDelete,
    selectedRowKeys,
    tiposDeRecargos,
    tiposDeActualizaciones,
    onFinish,
    deleteItem,
    handleOnRowClick,
    reset,
    validatePeriodoUnico,
    onClickAdd: () => { reset(); setVisible(true); },
    onClickEdit: () => setVisible(true),
    onClickDelete: () => setVisibleModalDelete(true),
  };
};

// ─── Reglas de validación (constante fuera del componente) ───────────────────

const REQUIRED_RULE = [{ required: true }];

// ─── Componente ──────────────────────────────────────────────────────────────

const TiposDeRecargosOmitidos = ({ permission, disabled = false }) => {
  const {
    form,
    loading,
    data,
    visible,
    visibleModalDelete,
    selectedRowKeys,
    tiposDeRecargos,
    tiposDeActualizaciones,
    onFinish,
    deleteItem,
    handleOnRowClick,
    reset,
    validatePeriodoUnico,
    onClickAdd,
    onClickEdit,
    onClickDelete,
  } = useTiposDeRecargosOmitidos();

  const columns = [
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 150,
      align: 'center',
      render: (val) => (val ? <Tag color="blue">{val}</Tag> : '—'),
    },
    {
      titleText: 'Rango de Fechas',
      key: '',
      dataIndex: '',
      width: 240,
      align: 'center',
      render: (_, record) => {
        const inicio = record.fecha_inicial?.format(DATE_FRONTEND_FORMAT);
        const fin = record.fecha_final?.format(DATE_FRONTEND_FORMAT);
        if (!inicio && !fin) return '—';
        return (
          <Space size={4}>
            <Tag color="default">{inicio ?? '—'}</Tag>
            <Typography.Text type="secondary">→</Typography.Text>
            <Tag color="default">{fin ?? '—'}</Tag>
          </Space>
        );
      },
    },
    {
      titleText: 'Tipos de Recargo',
      dataIndex: 'tipos_de_recargo',
      key: 'tipos_de_recargo',
      render: (val = []) => {
        if (!val.length) {
          return <Typography.Text type="secondary">Sin tipos de recargo</Typography.Text>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {val.map((_item) => (
              <Tag key={_item?.id ?? _item} color="geekblue">{_item?.descripcion}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      titleText: 'Tipos de Actualización',
      dataIndex: 'tipos_de_actualizaciones',
      key: 'tipos_de_actualizaciones',
      render: (val = []) => {
        if (!val.length) {
          return <Typography.Text type="secondary">Sin tipos de actualización</Typography.Text>;
        }
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {val.map((_item) => (
              <Tag key={_item?.id ?? _item} color="purple">{_item?.descripcion}</Tag>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Divider orientation="left">
          <Typography.Title level={4}>Accesorios Omitidos</Typography.Title>
        </Divider>
        <Table
          cols={columns}
          data={data}
          rowSelection={{ selectedRowKeys, type: 'radio' }}
          handleOnRowClick={handleOnRowClick}
          controls={{ onClickAdd, onClickEdit, onClickDelete }}
          rowKey="id"
          permission={permission}
          hasAllViewPermission={permission?.hasAllViewPermission}
          disabled={disabled}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          keyboard={!loading}
          maskClosable={!loading}
          width={600}
          forceRender
          centered
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} configuración de accesorios omitidos`}
              onCancel={reset}
              loading={loading}
              onFinish={onFinish}
              permission={permission}
            />
          )}
        >
          <Form
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
            layout="vertical"
            name="configuracion_accesorios_omitidos"
          >
            <Col span={24}>
              <NumericInput
                name="periodo"
                label="Periodo"
                hasFeedback
                required
                rules={[{ validator: validatePeriodoUnico }]}
                tooltip="Año fiscal en el que se omiten los accesorios configurados."
              />
            </Col>
            <Col span={24}>
              <Form.Item
                name="fechas"
                label="Rango de Fechas"
                tooltip="Ingrese el rango de fechas en el que se aplicará la regla de los accesorios configurados."
                hasFeedback
                rules={REQUIRED_RULE}
              >
                <DatePicker.RangePicker
                  format={[DATE_FRONTEND_FORMAT, DATE_FRONTEND_FORMAT]}
                  placeholder={['', '']}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="tipos_de_recargo"
                label="Tipos de Recargo"
                hasFeedback
                tooltip="Tipos de recargo que se omitirán en el rango de fechas configurado."
              >
                <Select dataSource={tiposDeRecargos} mode="multiple" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="tipos_de_actualizaciones"
                label="Tipos de Actualización"
                hasFeedback
                tooltip="Tipos de actualización que se omitirán en el rango de fechas configurado."
              >
                <Select dataSource={tiposDeActualizaciones} mode="multiple" />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={reset}
        visible={visibleModalDelete}
        loading={loading}
        content={`la configuración para la omisión de recargos del periodo ${form.getFieldValue('periodo')}`}
      />
    </Row>
  );
};

export default TiposDeRecargosOmitidos;
